import React, {useEffect, useMemo, useRef, useState} from 'react';
import {IonAvatar, IonIcon} from '@ionic/react';
import {pencilOutline} from 'ionicons/icons';

interface ProfilePictureProps {
    profilePicUrl?: string;
    fullName: string; // used to compute initial
    onFileChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    showEditable: boolean;
    width: number;
    height: number;
    bgColor?: string;
    textColor?: string;
}

const ProfilePicture: React.FC<ProfilePictureProps> = ({
                                                           profilePicUrl,
                                                           fullName,
                                                           onFileChange,
                                                           showEditable,
                                                           width,
                                                           height,
                                                           bgColor = '#9ca3af',   // Tailwind gray-400
                                                           textColor = '#ffffff',  // white
                                                       }) => {
    const [useFallback, setUseFallback] = useState<boolean>(!profilePicUrl);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Reset fallback if the URL changes
    useEffect(() => {
        setUseFallback(!profilePicUrl);
    }, [profilePicUrl]);

    const initial = useMemo(() => {
        const ch = fullName?.trim()?.charAt(0) || '?';
        return ch.toUpperCase();
    }, [fullName]);

    const fontSize = Math.floor(Math.min(width, height) / 2.1);

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onFileChange}
                style={{ display: 'none' }}
            />

            <IonAvatar
                style={{
                    position: 'relative',
                    width: `${width}px`,
                    height: `${height}px`,
                    overflow: 'hidden',
                }}
            >
                {!useFallback ? (
                    <img
                        alt={`${fullName}'s profile picture`}
                        src={profilePicUrl}
                        onError={() => setUseFallback(true)} // switch to fallback if broken
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                        }}
                    />
                ) : (
                    <div
                        role="img"
                        aria-label={`Avatar for ${fullName}`}
                        style={{
                            width: '100%',
                            height: '100%',
                            background: bgColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: `${fontSize}px`,
                            fontWeight: 600,
                            color: textColor,
                            userSelect: 'none',
                        }}
                    >
                        {initial}
                    </div>
                )}

                {showEditable && (
                    <IonIcon
                        icon={pencilOutline}
                        style={{
                            background: 'black',
                            color: 'white',
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            borderRadius: '50%',
                            width: '30px',
                            height: '30px',
                            display: 'grid',
                            placeItems: 'center',
                            cursor: 'pointer',
                            zIndex: 2,
                        }}
                        onClick={() => fileInputRef.current?.click()}
                    />
                )}
            </IonAvatar>
        </>
    );
};

export default ProfilePicture;
